/**
 * Vuex plugin to synchronize state with localStorage across multiple windows
 */

// Storage key prefix to avoid conflicts
const STORAGE_KEY_PREFIX = "chess_";

/**
 * Create a Vuex plugin that syncs specific state to localStorage
 * and listens for changes from other windows
 *
 * @param {Object} options - Configuration options
 * @returns {Function} Vuex plugin
 */
export default function createStorageSyncPlugin(options = {}) {
  // Default state paths to sync (can include module paths like "teacher.currentClass")
  const statesToSync = options.states || [
    "currentScenario",
    "currentTopicId",
    "scenarioState",
  ];

  // Flag to prevent infinite loops when updating from storage events
  let isUpdatingFromStorage = false;

  // Track previous state values to detect changes
  const previousStateValues = {};

  return (store) => {
    // Helper function to get nested state value
    const getStateValue = (state, path) => {
      return path.split(".").reduce((obj, key) => obj && obj[key], state);
    };

    // Helper function to get mutation name for a state path
    const getMutationName = (statePath) => {
      const parts = statePath.split(".");
      const lastPart = parts[parts.length - 1];
      return `set${lastPart.charAt(0).toUpperCase() + lastPart.slice(1)}`;
    };

    // Initialize state from localStorage if available
    statesToSync.forEach((statePath) => {
      const storageKey = `${STORAGE_KEY_PREFIX}${statePath}`;
      const storedValue = localStorage.getItem(storageKey);

      if (storedValue) {
        try {
          const value = JSON.parse(storedValue);
          const mutationName = getMutationName(statePath);

          if (typeof store.commit === "function") {
            isUpdatingFromStorage = true;
            store.commit(mutationName, value);
            isUpdatingFromStorage = false;
          }
        } catch (error) {
          console.error(`Error parsing stored value for ${statePath}:`, error);
        }
      }
    });

    // Listen for storage events from other windows
    window.addEventListener("storage", (event) => {
      // Check if the event is for one of our synced states
      statesToSync.forEach((statePath) => {
        const storageKey = `${STORAGE_KEY_PREFIX}${statePath}`;

        if (event.key === storageKey && event.newValue) {
          try {
            const value = JSON.parse(event.newValue);
            const mutationName = getMutationName(statePath);

            if (typeof store.commit === "function") {
              isUpdatingFromStorage = true;
              store.commit(mutationName, value);
              isUpdatingFromStorage = false;
            }
          } catch (error) {
            console.error(
              `Error handling storage event for ${statePath}:`,
              error
            );
          }
        }
      });
    });

    // Initialize previous state values
    statesToSync.forEach((statePath) => {
      previousStateValues[statePath] = getStateValue(store.state, statePath);
    });

    // Subscribe to store mutations to update localStorage
    store.subscribe((_mutation, state) => {
      statesToSync.forEach((statePath) => {
        try {
          const currentValue = getStateValue(state, statePath);
          const previousValue = previousStateValues[statePath];

          const currentJson = JSON.stringify(currentValue);
          const previousJson = JSON.stringify(previousValue);

          if (currentJson !== previousJson) {
            previousStateValues[statePath] = currentValue;

            if (!isUpdatingFromStorage) {
              const storageKey = `${STORAGE_KEY_PREFIX}${statePath}`;
              localStorage.setItem(storageKey, currentJson);
            }
          }
        } catch (error) {
          console.error(`Error storing value for ${statePath}:`, error);
        }
      });
    });
  };
}
